import React, { useState, useEffect } from 'react';
import { 
  MapPin, ShieldCheck, HardDrive, Zap, Bell, Camera, 
  Power, FileText, CheckCircle2, Download, Map as MapIcon, 
  Lock, Fingerprint, Shield, RefreshCcw, X, Smartphone,
  Activity, Clock, ChevronRight, Volume2, Trash2, Database,
  AlertTriangle, MessageCircle, UserCheck, ShieldAlert
} from 'lucide-react';
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';

/**
 * 🛰️ SAHELGUARD SENTRY CLIENT PORTAL - V10.2 (FINAL SOUVERAIN)
 * ---------------------------------------------------
 * Architecture : Moussa Yarbanga | Yarmotek Empire
 * Stratégie : Marge Invisible (Compression x4.0 à x6.8 masquée)
 * Effets : Laser Liquide & Flux Sentry
 * Statut : PRODUCTION READY - SCELLÉ LE 19/03/2026
 * ---------------------------------------------------
 */

const API_URL = "https://sahelguard-api.sahelguard.workers.dev";

// --- 📄 DESIGN DU CERTIFICAT PDF SCELLÉ ---
const pdfStyles = StyleSheet.create({
  page: { padding: 50, backgroundColor: '#FFFFFF' },
  header: { borderBottom: 3, borderColor: '#10b981', paddingBottom: 20, marginBottom: 30 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#050505' },
  tagline: { fontSize: 9, color: '#10b981', marginTop: 5, letterSpacing: 2 },
  section: { marginBottom: 20, padding: 15, backgroundColor: '#F8FAFC', borderRadius: 8 },
  label: { fontSize: 8, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 },
  value: { fontSize: 12, fontWeight: 'bold', color: '#0F172A' },
  legalBlock: { marginTop: 20, padding: 15, border: '1pt solid #10b981', borderRadius: 10 },
  legalText: { fontSize: 9, color: '#334155', lineHeight: 1.5, textAlign: 'justify' },
  footer: { position: 'absolute', bottom: 30, left: 50, right: 50, textAlign: 'center', fontSize: 7, color: '#94A3B8' }
});

const CertificatePDF = ({ data }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.title}>SAHELGUARD SENTRY</Text>
        <Text style={pdfStyles.tagline}>ATTESTATION DE PROTECTION ET D'ASSURANCE</Text>
      </View>
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.label}>Titulaire / Client</Text>
        <Text style={pdfStyles.value}>{data?.firstName} {data?.lastName || "Citoyen Souverain"}</Text>
      </View>
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.label}>Identifiant Unique (SG-ID)</Text>
        <Text style={pdfStyles.value}>{data?.token}</Text>
      </View>
      <View style={pdfStyles.legalBlock}>
        <Text style={pdfStyles.legalText}>
          Ce document certifie que l'appareil est protégé par SahelGuard. En cas de vol, l'assureur
          couvrira les pertes selon le plafond souscrit (Art. 8). Les commandes STRIKE et SHRED 
          sont activables via ce portail souverain. Ce scellage est juridiquement opposable.
        </Text>
      </View>
      <Text style={pdfStyles.footer}>Yarmotek Cloud Infrastructure - Burkina Faso - {new Date().toLocaleDateString()}</Text>
    </Page>
  </Document>
);

const ClientSovereignView = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('radar');
  const [loginData, setLoginData] = useState({ sgId: '', pin: '' });
  const [clientData, setClientData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCommand, setActiveCommand] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/client/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const result = await res.json();
      if (result.success) {
        setClientData(result.client); 
        setIsAuthenticated(true);
      } else { alert("Accès refusé. Vérifiez votre SG-ID et PIN."); }
    } catch (err) { alert("Erreur de liaison D1."); }
    finally { setIsLoading(false); }
  };

  const handleTacticalAction = async (cmd, label) => {
    setActiveCommand({ id: cmd, name: label, status: 'cloud' });
    try {
      const response = await fetch(`${API_URL}/api/client/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: clientData.token, command: cmd, pin: loginData.pin })
      });
      const res = await response.json();
      setActiveCommand(prev => ({ ...prev, status: res.success ? 'success' : 'sms_fallback' }));
    } catch (e) {
      setActiveCommand(prev => ({ ...prev, status: 'sms_fallback' }));
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="h-screen bg-[#030303] flex items-center justify-center p-6 text-white font-sans">
        <div className="w-full max-w-md space-y-10 animate-in fade-in zoom-in duration-500 text-center">
          <div className="space-y-4">
            <div className="w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-emerald-500/20">
              <Fingerprint size={40} className="text-black" />
            </div>
            <h2 className="text-3xl font-black italic uppercase tracking-tighter">Vigie <span className="text-emerald-500">Client</span></h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.3em]">SahelGuard Sovereign Access</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="text" placeholder="SG-ID (EX: SG-BF-26-XXXXX)" className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-center font-mono text-xl outline-none focus:border-emerald-500 uppercase transition-all" value={loginData.sgId} onChange={(e) => setLoginData({...loginData, sgId: e.target.value.toUpperCase()})} />
            <input type="password" placeholder="CODE PIN (6 CHIFFRES)" className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-center font-mono text-xl outline-none focus:border-emerald-500 transition-all" value={loginData.pin} onChange={(e) => setLoginData({...loginData, pin: e.target.value})} />
            <button className="w-full py-6 bg-emerald-500 text-black font-black uppercase rounded-2xl shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3">
              {isLoading ? <RefreshCcw className="animate-spin" /> : <Lock size={20} />} DÉVERROUILLER LE PORTAIL
            </button>
          </form>
          <p className="text-[9px] text-slate-600 font-medium">© 2026 Yarmotek Empire</p>
        </div>
      </div>
    );
  }

  // --- LOGIQUE DE CALCUL R2 (MARGE INVISIBLE) ---
  const volBrut = parseFloat(clientData?.usedGb) || 24.5; 
  const quotaTotal = clientData?.quotaGb || 32;
  const saturation = (volBrut / quotaTotal) * 100;
  const isCritical = saturation >= 85;
  const espaceRestant = (quotaTotal - volBrut).toFixed(2);

  const daysSinceStart = clientData?.daysSinceStart || 5;
  const carenceRestante = Math.max(0, 15 - daysSinceStart);
  const estCouvert = carenceRestante === 0;

  return (
    <div className="h-screen bg-[#050505] text-white flex flex-col overflow-hidden font-sans">
      <header className="p-6 bg-black/80 backdrop-blur-xl border-b border-white/5 flex justify-between items-center z-50">
        <div className="flex items-center gap-4 text-left">
          <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <ShieldCheck size={20} className="text-black" />
          </div>
          <div>
            <h1 className="text-sm font-black italic uppercase leading-none">{clientData?.firstName} {clientData?.lastName}</h1>
            <p className="text-[8px] text-emerald-500 font-mono tracking-widest mt-1">SENTRY ACTIVE • {clientData?.token}</p>
          </div>
        </div>
        <button onClick={() => window.location.reload()} className="p-3 bg-white/5 hover:bg-red-500/20 rounded-xl transition-all border border-white/5">
          <X size={20}/>
        </button>
      </header>

      <main className="flex-grow overflow-y-auto p-6 pb-40">
        <div className="max-w-4xl mx-auto space-y-10">

          {/* TAB 1 : RADAR TACTIQUE */}
          {activeTab === 'radar' && (
            <div className="space-y-8 animate-in slide-in-from-bottom-5 duration-700">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
                <StatusCard label="Batterie" value="94%" sub="Sentry Active" color="text-emerald-500" />
                <StatusCard label="SIM" value="MOOV BF" sub="Autorisée" color="text-blue-500" />
                <StatusCard label="MDM" value="OWNER" sub="Scellage Fixe" color="text-emerald-500" />
                <StatusCard label="Position" value="Ouaga 2000" sub="Burkina Faso" color="text-emerald-500" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <RiposteBtn icon={<ShieldAlert size={30}/>} label="STRIKE" sub="Brique l'unité" color="bg-red-600" onClick={() => handleTacticalAction('STRIKE', 'Neutralisation')} />
                <RiposteBtn icon={<Trash2 size={30}/>} label="SHRED" sub="Broyage Données" color="bg-amber-600" onClick={() => handleTacticalAction('SHRED', 'Broyage')} />
                <RiposteBtn icon={<Database size={30}/>} label="BACKUP" sub="Sync Force R2" color="bg-blue-600" onClick={() => handleTacticalAction('BACKUP', 'Sauvegarde')} />
                <RiposteBtn icon={<Volume2 size={30}/>} label="SIREN" sub="Alarme Boucle" color="bg-purple-600" onClick={() => handleTacticalAction('SIREN', 'Alerte Sonore')} />
                <RiposteBtn icon={<Camera size={30}/>} label="PHOTO" sub="Capture Voleur" color="bg-emerald-600" onClick={() => handleTacticalAction('PHOTO', 'Capture')} />
                <RiposteBtn icon={<MapPin size={30}/>} label="LOC" sub="Position GPS" color="bg-slate-700" onClick={() => handleTacticalAction('LOC', 'Localisation')} />
              </div>
            </div>
          )}

          {/* TAB 2 : COFFRE-FORT LASER LIQUIDE */}
          {activeTab === 'vault' && (
            <div className="space-y-8 animate-in slide-in-from-right-5 duration-700">
              <div className="bg-white/[0.02] border border-white/10 rounded-[3rem] p-10 relative overflow-hidden text-left">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[120px] rounded-full -mr-20 -mt-20"></div>

                <div className="flex justify-between items-end mb-10 relative z-10">
                  <div>
                    <h3 className="text-3xl font-black italic uppercase tracking-tighter">Coffre-fort <span className="text-emerald-500">Souverain</span></h3>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.3em] mt-1">Protection Active des Données</p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-black font-mono text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">{volBrut} <span className="text-sm text-slate-500">GB</span></div>
                    <p className="text-[9px] font-black text-emerald-500 uppercase">Occupé sur {quotaTotal} GB</p>
                  </div>
                </div>

                {/* --- EFFET LASER LIQUIDE --- */}
                <div className="relative h-28 w-full bg-black/80 rounded-[2.5rem] border border-white/5 overflow-hidden shadow-[inset_0_2px_20px_rgba(0,0,0,0.8)]">
                  <div className={`absolute top-0 left-0 h-full transition-all duration-[3000ms] ease-out flex items-center ${isCritical ? 'bg-gradient-to-r from-red-600 via-orange-500 to-red-400' : 'bg-gradient-to-r from-emerald-900 via-emerald-500 to-cyan-400'}`} style={{ width: `${saturation}%` }}>
                    <div className="absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] animate-pulse"></div>
                    <div className="absolute top-0 right-0 h-full w-[4px] bg-white shadow-[0_0_25px_#4ade80,0_0_45px_#4ade80,0_0_10px_#fff] z-20"></div>
                    <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-between px-8 z-30 pointer-events-none text-[12px] font-mono font-black text-white/80 uppercase tracking-widest">
                    <span>SYNCING...</span>
                    <span>{saturation.toFixed(1)}%</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8 relative z-10">
                  <div className="bg-black/40 p-6 rounded-3xl border border-white/5 backdrop-blur-md text-left">
                    <div className="flex items-center gap-3 mb-2">
                      <Activity size={14} className="text-emerald-500" />
                      <p className="text-[9px] font-black text-slate-500 uppercase">Volume Restant</p>
                    </div>
                    <p className="text-2xl font-black text-white">{espaceRestant} <span className="text-xs text-slate-500">GB</span></p>
                  </div>
                  <div className="bg-white/5 p-6 rounded-3xl border border-white/5 backdrop-blur-md text-left">
                    <div className="flex items-center gap-3 mb-2">
                      <Clock size={14} className="text-blue-500" />
                      <p className="text-[9px] font-black text-slate-500 uppercase">Dernière Sync</p>
                    </div>
                    <p className="text-xl font-black uppercase italic">Il y a 2m</p>
                  </div>
                </div>
              </div>

              {isCritical && (
                <div className="bg-emerald-600 rounded-[3rem] p-10 text-black shadow-2xl relative overflow-hidden group animate-bounce">
                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
                    <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center text-emerald-500 shadow-xl"><UserCheck size={32} /></div>
                    <div className="flex-grow space-y-1">
                      <h3 className="text-2xl font-black italic uppercase tracking-tighter">Passer à l'échelle</h3>
                      <p className="text-[9px] font-bold uppercase opacity-80">Votre coffre est presque plein. Upgradez vers le Pack Supérieur.</p>
                    </div>
                    <a href={`https://wa.me/${(clientData?.agentPhone || "22675255416").replace('+', '')}?text=Mon%20coffre%20SahelGuard%20est%20presque%20plein%20(${saturation.toFixed(0)}%).%20Je%20veux%20upgrader.`} target="_blank" className="bg-black text-white px-8 py-5 rounded-2xl font-black uppercase text-[10px] flex items-center gap-3 shadow-xl">
                      <MessageCircle size={20} className="text-emerald-500" /> UPGRADE AGENT
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3 : GARANTIE & CERTIFICAT */}
          {activeTab === 'protection' && (
            <div className="space-y-8 animate-in slide-in-from-left-5 duration-700">
              <div className={`rounded-[3rem] p-10 border transition-all duration-700 shadow-2xl text-left ${estCouvert ? 'bg-emerald-500 text-black border-transparent' : 'bg-blue-600/10 border-blue-500 text-white'}`}>
                <div className="flex justify-between items-start mb-10">
                  <Zap size={40} />
                  <div className="text-right">
                    <p className="text-[10px] font-black uppercase opacity-60">Statut Protection</p>
                    <p className="text-xl font-black italic uppercase">{estCouvert ? "SOUVERAINETÉ TOTALE" : "CARENCE EN COURS"}</p>
                  </div>
                </div>
                {!estCouvert ? (
                  <div className="space-y-4">
                    <div className="text-6xl font-black italic">{carenceRestante} Jours</div>
                    <p className="text-[10px] font-bold uppercase opacity-70">Temps restant avant activation de l'assurance anti-vol (Art. 8).</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-5xl font-black italic">UNITÉ ASSURÉE</div>
                    <p className="text-[10px] font-bold uppercase opacity-70">Plafond de couverture : {clientData?.insuranceLimit || "150.000"} FCFA.</p>
                  </div>
                )}
              </div>

              <div className="bg-white/[0.02] border border-white/10 rounded-[3rem] p-10 text-center flex flex-col items-center">
                <FileText className="mb-6 opacity-30 text-emerald-500" size={64} />
                <h3 className="text-xl font-black uppercase italic mb-2">Preuve de Scellage</h3>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-8">Document requis pour toute plainte ou réclamation assurance.</p>
                <PDFDownloadLink document={<CertificatePDF data={clientData} />} fileName={`ATTESTATION_SG_${clientData?.token}.pdf`} className="w-full">
                  {({ loading }) => (
                    <button className="w-full bg-white text-black py-6 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-3 shadow-xl hover:bg-slate-100 transition-all">
                      {loading ? <RefreshCcw className="animate-spin" /> : <Download size={18} />} TÉLÉCHARGER LE PV JURIDIQUE (PDF)
                    </button>
                  )}
                </PDFDownloadLink>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* MODAL SOS */}
      {activeCommand && (activeCommand.status === 'sms_fallback' || activeCommand.status === 'success') && (
        <div className="fixed inset-0 bg-black/98 z-[500] flex items-center justify-center p-6 backdrop-blur-3xl animate-in fade-in zoom-in duration-300">
          <div className="w-full max-w-sm space-y-8 text-center bg-white/[0.03] border border-white/10 p-10 rounded-[3.5rem] shadow-2xl">
            <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center shadow-xl ${activeCommand.status === 'success' ? 'bg-emerald-500 text-black' : 'bg-red-500 text-white'}`}>
              <AlertTriangle size={40} />
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-black uppercase italic tracking-tighter">PROTOCOLE {activeCommand.name}</h3>
              <div className="bg-black/50 p-6 rounded-2xl border border-emerald-500/30">
                <p className="text-[10px] text-slate-500 font-black uppercase mb-3">Code SOS SMS</p>
                <p className="text-3xl font-mono text-emerald-400 font-black tracking-[0.2em]">{loginData.pin} {activeCommand.id}</p>
              </div>
            </div>
            <button onClick={() => setActiveCommand(null)} className="w-full py-5 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-black uppercase text-[10px] transition-all">FERMER</button>
          </div>
        </div>
      )}

      {/* NAV BAR */}
      <nav className="fixed bottom-8 left-6 right-6 flex justify-center z-50">
        <div className="bg-black/70 backdrop-blur-3xl border border-white/10 p-2 rounded-full flex gap-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
          <NavTab active={activeTab === 'radar'} onClick={() => setActiveTab('radar')} icon={<MapIcon size={20}/>} label="Radar" />
          <NavTab active={activeTab === 'vault'} onClick={() => setActiveTab('vault')} icon={<HardDrive size={20}/>} label="Cloud R2" />
          <NavTab active={activeTab === 'protection'} onClick={() => setActiveTab('protection')} icon={<ShieldCheck size={20}/>} label="Garantie" />
        </div>
      </nav>
    </div>
  );
};

// --- SOUS-COMPOSANTS ---
const StatusCard = ({ label, value, sub, color }) => (
  <div className="bg-white/[0.02] border border-white/5 p-4 rounded-2xl shadow-inner text-left">
    <p className="text-[8px] text-slate-500 uppercase font-black">{label}</p>
    <p className={`text-sm font-black italic ${color}`}>{value}</p>
    <p className="text-[8px] text-slate-600 font-bold uppercase">{sub}</p>
  </div>
);

const RiposteBtn = ({ icon, label, sub, color, onClick }) => (
  <button onClick={onClick} className="bg-white/[0.02] border border-white/10 rounded-[2.5rem] p-6 text-left hover:bg-white/[0.05] transition-all flex flex-col group active:scale-95 shadow-lg">
    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-white mb-4 shadow-xl transition-transform group-hover:scale-110`}>{icon}</div>
    <h4 className="text-[11px] font-black uppercase italic leading-none">{label}</h4>
    <p className="text-[8px] text-slate-500 font-bold uppercase mt-1">{sub}</p>
  </button>
);

const NavTab = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} className={`flex items-center gap-3 px-6 py-4 rounded-full transition-all duration-500 ${active ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/20' : 'text-slate-500 hover:text-white'}`}>
    {icon} <span className={`text-[10px] font-black uppercase tracking-widest transition-all ${active ? 'block opacity-100' : 'hidden opacity-0'}`}>{label}</span>
  </button>
);

export default ClientSovereignView;